import React from "react";
import axios from "axios";
import { Table, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import GLOBALVAR from "../../constants";
import { format } from 'date-fns';
import LoadingSpinner from "../../components/misc/LoadingSpinner";
import jsPDF from "jspdf";
import 'jspdf-autotable';
import './css/ListPaginationPayments.scss'; 
import ToPeso from "./ToPeso";
import logo from "../../logo.png";

function ListPaginationPayments() {
    const [paymentHeaders, setPaymentHeaders] = useState([]);
    const [paymentLists, setPaymentLists] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [count, setCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const fetchPayments = async (currentPage, itemsPerPage, startDate, endDate) => {
        setShowSpinner(true);

        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/payments/fetch/all`, {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined
                }
            });

            const paymentsData = response.data.rows.reduce((acc, item) => {
                const fullName = `${item.loanProcessing.applicant.lastName}, ${item.loanProcessing.applicant.firstName} ${item.loanProcessing.applicant.middleName}`;
                const fullNameWithLink = (
                    <a href={`/applicant/${item.loanProcessing.applicant.id}`}>
                        {fullName}
                    </a>
                );

                const loansnum = item.loanProcessingID.toString().padStart(6, '0');
                const loansNumWithLink = (
                    <a href={`/loans/${item.loanProcessing.id}`}>
                        {loansnum}
                    </a>
                );
            
                acc[item.id] = {
                    items: [
                        { key: 'paymentDate', value: format(new Date(item.paymentDate), 'MM/dd/yyyy') },
                        { key: 'fullName', value: fullNameWithLink },
                        { key: 'loanProcessingID', value: loansNumWithLink },
                        { key: 'prNumber', value: item.prNumber },
                        { key: 'orNumber', value: item.orNumber },
                        { key: 'paymentType', value: item.paymentType },
                        { key: 'chequeNumber', value: item.chequeNumber },
                        { key: 'paidPrincipal', value: <ToPeso amount={item.paidPrincipal} /> },
                        { key: 'paidInterest', value: <ToPeso amount={item.paidInterest} /> },
                        { key: 'paidPenalty', value: <ToPeso amount={item.paidPenalty} /> },
                        { key: 'paidOther', value: <ToPeso amount={item.paidOther} /> },
                        { key: 'paidTotal', value: <ToPeso amount={item.paidTotal} /> }
                    ]
                };
                return acc;
            }, {});                    

            setCount(response.data.count);
            setPaymentLists(paymentsData);

            const paymentHeaders = [
                { value: "Date" },
                { value: "Full Name" },
                { value: "Loan #" },
                { value: "PR #" },
                { value: "OR #" },
                { value: "Payment Type" },
                { value: "Cheque No." },
                { value: "Paid Principal" },
                { value: "Paid Interest" },
                { value: "Paid Penalty" },
                { value: "Paid Other" },
                { value: "Paid Total" }
            ];

            setPaymentHeaders(paymentHeaders);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setShowSpinner(false);
        }
    };

    const btnPrev = () => {
        setCurrentPage(prevPage => prevPage - 1);
    };

    const btnNext = () => {
        setCurrentPage(prevPage => prevPage + 1);
    };

    const btnPage = (page) => {
        setCurrentPage(page);
    };

    const btnFirst = () => {
        setCurrentPage(1);
    };

    const btnLast = () => {
        const totalPages = Math.ceil(count / itemsPerPage);
        setCurrentPage(totalPages);
    };

    const totalPages = Math.ceil(count / itemsPerPage);

    const getPageNumbers = () => {
        const pageNumbers = [];
        const totalPagesToShow = 3;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const lastPage = currentPage + 1 > totalPages ? totalPages : currentPage + 1;
            const firstPage = lastPage - (totalPagesToShow - 1) > 0 ? lastPage - (totalPagesToShow - 1) : 1;
            const dots = currentPage + 1 < totalPages ? '...' : '';

            for (let i = firstPage; i <= lastPage; i++) {
                pageNumbers.push(i);
            }

            if (dots) {
                pageNumbers.push(dots);
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    useEffect(() => {
        fetchPayments(currentPage, itemsPerPage, startDate, endDate);
    }, [currentPage, itemsPerPage, startDate, endDate]); // Call fetchPayments whenever currentPage, itemsPerPage, startDate, or endDate changes

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to the first page whenever itemsPerPage changes
    };

    const handleGeneratePDF = async () => {
        setShowSpinner(true);
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/payments/fetch/all`, {
                params: {
                    page: 1,
                    limit: count, // Fetch all results
                    startDate: startDate || undefined,
                    endDate: endDate || undefined
                }
            });
    
            const doc = new jsPDF('landscape');

            // Function to center text
            const centerText = (text, y, fontWeight = 'normal') => {
               // Set font style
                doc.setFont(undefined, fontWeight);

                // Get page width
                const pageWidth = doc.internal.pageSize.getWidth();
                // Calculate the text width
                const textWidth = doc.getTextDimensions(text).w;
                // Calculate the text's X coordinate to be centered
                const textX = (pageWidth - textWidth) / 2;
                // Add the text to the document
                doc.text(text, textX, y);
            }

            const formatDate = (date) => {
                const d = new Date(date);
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const year = d.getFullYear();
                return `${month}/${day}/${year}`;
            }
    
            // Add company logo
            const logoImg = await fetch(logo);
            const logoBlob = await logoImg.blob();
            const logoURL = URL.createObjectURL(logoBlob);
            doc.addImage(logoURL, 'PNG', 10, 3, 70, 14.35); // Adjust position and dimensions as needed
    
            // Add document heading
            doc.setFontSize(14);
            centerText("Daily Loan Repayments - Detailed", 10, 'bold');
    
            if(startDate && endDate){
                doc.setFontSize(12);
                centerText(`${formatDate(startDate)} - ${formatDate(endDate)}`, 19);
            }
    
            const tableColumn = paymentHeaders.map(header => header.value);
            const tableRows = [];
    
            response.data.rows.forEach(item => {
                const rowData = [
                    format(new Date(item.paymentDate), 'MM/dd/yyyy'),
                    `${item.loanProcessing.applicant.lastName}, ${item.loanProcessing.applicant.firstName} ${item.loanProcessing.applicant.middleName}`,
                    item.loanProcessingID.toString().padStart(6, '0'),
                    item.prNumber,
                    item.orNumber,
                    item.paymentType,
                    item.chequeNumber,
                    item.paidPrincipal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    item.paidInterest.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    item.paidPenalty.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    item.paidOther.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    item.paidTotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                ];
                tableRows.push(rowData);
            });
    
            // Get current timestamp and format it
            const now = new Date();
            const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
            doc.autoTable(tableColumn, tableRows, { startY: 30 }); // Adjust startY position as needed
            doc.save(`Daily_Loan_Repayments_Detailed_${timestamp}.pdf`); // Save with timestamp in filename
            setShowSpinner(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSpinner(false);
        }
    };            

    const handleClick = (e) => {
        e.preventDefault();
        handleGeneratePDF();
    };

    return (
        <>
        {showSpinner && (<LoadingSpinner />)}
        <div className="filters-container">
            <div className="pagination-controls">
                <label htmlFor="itemsPerPage">Show: </label>
                <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                </select>
            </div>
            <div className="date-range-controls">
                <label htmlFor="startDate">Range: </label>
                <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />
            </div>
            <Button href="#" onClick={handleClick} className="generate-pdf-button">
                <i className="fa-solid fa-print"></i> Export
            </Button>
        </div>
        <Table className="list-container loan-repayments" hover responsive>
            <thead className="list-title">
                <tr>
                {
                    paymentHeaders.map((item, index) => {
                        return(
                            <th className="list-header-item" key={index}>{item.value}</th>
                        );
                    })
                }
                </tr>
            </thead>
            <tbody>
            {
                Object.values(paymentLists).reverse().map((item, index) => (
                    <tr className="list-body" key={index}>
                        {item.items.map((subitem, subindex) => (
                            <td className="list-body-item" key={`${index}+${subindex}`}>
                                {subitem.value}
                            </td>
                        ))}
                    </tr>
                ))
            }
            </tbody>
            <tfoot>
                <tr className="page-buttons">
                    <td colSpan="12">
                        <div className="page-buttons__container">
                            <button onClick={btnFirst} disabled={currentPage === 1}>
                                <i className="fa-solid fa-angles-left"></i>
                            </button>
                            <button onClick={btnPrev} disabled={currentPage === 1}>
                                <i className="fa-solid fa-angle-left"></i>
                            </button>
                            {getPageNumbers().map((page, index) => (
                                <React.Fragment key={index}>
                                    {page === '...' ? (
                                        <span className="elip">
                                            <i className="fa-solid fa-ellipsis"></i>
                                        </span>
                                    ) : (
                                        <span className="number">
                                            <button onClick={() => btnPage(page)} disabled={currentPage === page}>
                                                {page}
                                            </button>
                                        </span>
                                    )}
                                </React.Fragment>
                            ))}
                            <button onClick={btnNext} disabled={currentPage === totalPages}>
                                <i className="fa-solid fa-angle-right"></i>
                            </button>
                            <button onClick={btnLast} disabled={currentPage === totalPages}>
                                <i className="fa-solid fa-angles-right"></i>
                            </button>
                        </div>
                    </td>
                </tr>
            </tfoot>
        </Table>
        </>
    );
}

export default ListPaginationPayments;
